import React, { useState, useEffect } from 'react';
import { api } from '../../api.js';
import RelatedLoadsTab from './RelatedLoadsTab.jsx';
import PhoneInput from '../../components/PhoneInput.jsx';

const blank = { name: '', ownerContactName: '', contactPhone: '', contactEmail: '', baseRegion: '', status: 'Active', startDate: '', notes: '' };

function fromFleet(f) {
  if (!f) return blank;
  return {
    name: f.name || '', ownerContactName: f.owner_contact_name || '', contactPhone: f.contact_phone || '',
    contactEmail: f.contact_email || '', baseRegion: f.base_region || '', status: f.status || 'Active',
    startDate: f.start_date || '', notes: f.notes || ''
  };
}

const TABS = ['Basic Information', 'Trucks', 'Trailers', 'Related Loads'];

export default function FleetFormModal({ fleet, onClose, onSaved }) {
  const [savedId, setSavedId] = useState(fleet?.id || null);
  const [form, setForm] = useState(() => fromFleet(fleet));
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // ── Trucks (tab 1) ───────────────────────────────────────────────────────
  const [truckData, setTruckData] = useState(null);
  const [addTruckId, setAddTruckId] = useState('');
  const loadTrucks = () => { if (savedId) api(`/dispatch/fleets/${savedId}/trucks`).then(setTruckData); };
  useEffect(() => { if (tab === 1 && savedId) loadTrucks(); }, [tab, savedId]);

  const addTruck = async () => {
    if (!addTruckId) return;
    await api(`/dispatch/fleets/${savedId}/trucks/${addTruckId}`, { method: 'POST' });
    setAddTruckId('');
    loadTrucks();
  };
  const removeTruck = async (id) => {
    await api(`/dispatch/fleets/${savedId}/trucks/${id}`, { method: 'DELETE' });
    loadTrucks();
  };

  // ── Trailers (tab 2) ─────────────────────────────────────────────────────
  const [trailerData, setTrailerData] = useState(null);
  const [addTrailerId, setAddTrailerId] = useState('');
  const loadTrailers = () => { if (savedId) api(`/dispatch/fleets/${savedId}/trailers`).then(setTrailerData); };
  useEffect(() => { if (tab === 2 && savedId) loadTrailers(); }, [tab, savedId]);

  const addTrailer = async () => {
    if (!addTrailerId) return;
    await api(`/dispatch/fleets/${savedId}/trailers/${addTrailerId}`, { method: 'POST' });
    setAddTrailerId('');
    loadTrailers();
  };
  const removeTrailer = async (id) => {
    await api(`/dispatch/fleets/${savedId}/trailers/${id}`, { method: 'DELETE' });
    loadTrailers();
  };

  const persist = async () => {
    if (!form.name.trim()) { setError('Fleet name is required.'); return null; }
    setSaving(true);
    setError(null);
    try {
      const saved = savedId
        ? await api(`/dispatch/fleets/${savedId}`, { method: 'PATCH', body: form })
        : await api('/dispatch/fleets', { method: 'POST', body: form });
      setSavedId(saved.id);
      return saved;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveAndClose = async () => { if (await persist()) onSaved(); };
  const saveAndNext = async () => { if (await persist()) setTab(t => Math.min(t + 1, TABS.length - 1)); };
  const reset = () => setForm(fleet ? fromFleet(fleet) : blank);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal customer-modal" onClick={e => e.stopPropagation()}>
        <div className="customer-modal-title">{fleet ? 'Edit fleet' : 'New fleet'}</div>

        <div className="customer-modal-tabs">
          <div className="form-tabs" style={{ marginBottom: 0 }}>
            {TABS.map((t, i) => (
              <button key={t} type="button" className={i === tab ? 'form-tab active' : 'form-tab'} onClick={() => setTab(i)}>{t}</button>
            ))}
          </div>
        </div>

        <div className="customer-modal-body">
          {tab === 0 && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Fleet Name<br/><input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Fleet One" /></label>
                <label>Base Region<br/><input value={form.baseRegion} onChange={e => set('baseRegion', e.target.value)} placeholder="e.g. Philadelphia, PA" /></label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Owner / Contact Name<br/><input value={form.ownerContactName} onChange={e => set('ownerContactName', e.target.value)} /></label>
                <label>Contact Phone<br/><PhoneInput value={form.contactPhone} onChange={v => set('contactPhone', v)} /></label>
                <label>Contact Email<br/><input value={form.contactEmail} onChange={e => set('contactEmail', e.target.value)} /></label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Status<br/>
                  <select value={form.status} onChange={e => set('status', e.target.value)}>
                    <option>Active</option><option>Inactive</option>
                  </select>
                </label>
                <label>Start Date<br/><input type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} /></label>
              </div>

              <label style={{ display: 'block' }}>Notes<br/><input value={form.notes} onChange={e => set('notes', e.target.value)} /></label>
            </>
          )}

          {tab === 1 && (
            !savedId ? <div className="empty">Save the fleet first to assign trucks.</div> : !truckData ? <div className="empty">Loading...</div> : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, marginBottom: 18 }}>
                  <select value={addTruckId} onChange={e => setAddTruckId(e.target.value)}>
                    <option value="">Select an available truck...</option>
                    {truckData.available.map(t => (
                      <option key={t.id} value={t.id}>{t.unit_number} — {[t.make, t.model].filter(Boolean).join(' ')}</option>
                    ))}
                  </select>
                  <button type="button" className="primary-btn" onClick={addTruck}>+ Add to fleet</button>
                </div>
                {truckData.assigned.length === 0 ? <div className="empty">No trucks in this fleet yet.</div> : (
                  <table>
                    <thead><tr><th>Unit No</th><th>Make / Model</th><th>Plate</th><th></th></tr></thead>
                    <tbody>
                      {truckData.assigned.map(t => (
                        <tr key={t.id}>
                          <td>{t.unit_number}</td>
                          <td>{[t.make, t.model].filter(Boolean).join(' ') || '—'}</td>
                          <td>{t.plate || '—'}</td>
                          <td><button type="button" onClick={() => removeTruck(t.id)} style={{ color: 'var(--red)' }}>Remove</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )
          )}

          {tab === 2 && (
            !savedId ? <div className="empty">Save the fleet first to assign trailers.</div> : !trailerData ? <div className="empty">Loading...</div> : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, marginBottom: 18 }}>
                  <select value={addTrailerId} onChange={e => setAddTrailerId(e.target.value)}>
                    <option value="">Select an available trailer...</option>
                    {trailerData.available.map(t => (
                      <option key={t.id} value={t.id}>{t.unit_number} — {t.trailer_type || 'no type set'}</option>
                    ))}
                  </select>
                  <button type="button" className="primary-btn" onClick={addTrailer}>+ Add to fleet</button>
                </div>
                {trailerData.assigned.length === 0 ? <div className="empty">No trailers in this fleet yet.</div> : (
                  <table>
                    <thead><tr><th>Unit No</th><th>Type</th><th>Plate</th><th></th></tr></thead>
                    <tbody>
                      {trailerData.assigned.map(t => (
                        <tr key={t.id}>
                          <td>{t.unit_number}</td>
                          <td>{t.trailer_type || '—'}</td>
                          <td>{t.plate || '—'}</td>
                          <td><button type="button" onClick={() => removeTrailer(t.id)} style={{ color: 'var(--red)' }}>Remove</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )
          )}

          {tab === 3 && (
            !savedId ? <div className="empty">No loads hauled by this fleet yet.</div> : <RelatedLoadsTab endpoint={`/dispatch/fleets/${savedId}/loads`} />
          )}

          {error && <p style={{ color: '#c4364b', fontSize: 13, margin: '16px 0 0' }}>{error}</p>}
        </div>

        <div className="customer-modal-footer">
          <div className="modal-actions" style={{ margin: 0 }}>
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="button" onClick={reset}>Reset</button>
            <button type="button" className="primary-btn" onClick={saveAndNext} disabled={saving || tab === TABS.length - 1}>Save &amp; Next</button>
            <button type="button" className="primary-btn" onClick={saveAndClose} disabled={saving}>
              {saving ? 'Saving...' : fleet ? 'Save changes' : 'Create fleet'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
